/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import shortid from 'shortid';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';

import formStyles from 'modules/form.module.scss';
import styles from './FormField.module.scss';

const groupFields = ['checkbox_group', 'radio_group'];

/**
 * This is the FormField component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function FormField({
  type,
  className,
  inputOnly,
  oneLine,
  position,
  required,
  errors,
  instructions,
  handle,
  value,
  checked,
  label,
  placeholder,
  options,
  onChange,
  min,
  max,
  maxLength,
  step,
  standalone,
  disabled,
  classNames: extraClasses,
  id: formId,
  initialErrors,
  ...props
}) {
  const [t, i18n] = useTranslation('common');
  const [id] = useState(formId ?? shortid.generate());
  const [fieldHandle] = useState(handle ?? id);
  const [touched, setTouched] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const columnClass = classNames(
    formStyles.column,
    formStyles[`column-${type}`],
    styles.column,
    styles[`column-${type}`],
    className,
    {
      [formStyles.standalone]: standalone,
      [formStyles.columnInputOnly]: inputOnly,
      [formStyles.columnGroup]: groupFields.includes(type),
      [formStyles.columnGroupOneLine]: oneLine,
      [formStyles[`column-content-align-${position}`]]: type === 'submit',
    },
    extraClasses && extraClasses.column ? extraClasses.column ?? null : null
  );

  const labelClass = classNames(
    formStyles.label,
    styles.label,
    {
      [formStyles.labelInputOnly]: inputOnly,
      [formStyles.labelRequired]: required,
      [formStyles.labelHasErrors]: touched && errors && errors.length > 0,
    },
    extraClasses && extraClasses.label ? extraClasses.label ?? null : null
  );

  const fieldClass = classNames(
    {
      [formStyles.input]: type !== 'submit',
      [formStyles.submit]: type === 'submit',
      [styles.input]: type !== 'submit',
      [styles.submit]: type === 'submit',
    },
    extraClasses && extraClasses.field ? extraClasses.field ?? null : null
  );

  const renderLabel = () => {
    if (!label) {
      return null;
    }

    return (
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
    );
  };

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          id={id}
          className={fieldClass}
          name={fieldHandle}
          rows="4"
          placeholder={placeholder}
          defaultValue={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setHasFocus(true);
            setTouched(true);
          }}
          onBlur={() => setHasFocus(false)}
          disabled={disabled}
          {...props}
        />
      );
    }

    if (type === 'select') {
      return (
        <select
          id={id}
          className={fieldClass}
          name={fieldHandle}
          rows="3"
          placeholder={placeholder}
          defaultValue={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setHasFocus(true);
            setTouched(true);
          }}
          onBlur={() => setHasFocus(false)}
          disabled={disabled}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id={id}
        className={fieldClass}
        type={type}
        name={fieldHandle}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          setHasFocus(true);
          setTouched(true);
        }}
        onBlur={() => setHasFocus(false)}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        disabled={disabled}
        data-lpignore={true}
        {...props}
      />
    );
  };

  const renderInstructions = () => {
    if (!instructions) {
      return null;
    }

    return (
      <div className={classNames(formStyles.instructions, styles.instructions)}>
        {instructions}
      </div>
    );
  };

  const renderErrors = () => {
    // Don't render errors when field has focus or hasn't been touched
    if ((!touched && !initialErrors) || hasFocus) {
      return null;
    }

    let visibleErrors = errors;

    // If field is required, empty and don't have focus - override errors to show error that field must be filled out
    if (
      required &&
      (!value || (typeof value === 'object' && isEmpty(value))) &&
      !hasFocus
    ) {
      visibleErrors = [t('form.required_but_empty')];
    }

    // Render nothing if there are no errors
    if (!visibleErrors || visibleErrors.length === 0) {
      return null;
    }

    return (
      <ul className={classNames('errors', formStyles.errors)}>
        {visibleErrors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    );
  };

  const renderField = () => {
    switch (true) {
      case type === 'checkbox':
        return (
          <>
            <label className={labelClass}>
              <input
                type="checkbox"
                name={fieldHandle}
                value={value}
                className={fieldClass}
                checked={checked}
                disabled={disabled}
                onFocus={() => {
                  setHasFocus(true);
                  setTouched(true);
                }}
                onBlur={() => setHasFocus(false)}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
              />
              <span>{label}</span>
            </label>
            {renderErrors()}
            {renderInstructions()}
          </>
        );

      case type === 'file':
        // return 'file';
        return (
          <>
            {renderLabel()}
            <div className={formStyles.fileButton}>{renderInput()}</div>
            {renderInstructions()}
            {renderErrors()}
          </>
        );

      case groupFields.includes(type): {
        const renderGroupContent = () => {
          if (!options) {
            return null;
          }

          return options.map((option, index) => {
            const isChecked = Array.isArray(value)
              ? value.includes(option.value)
              : value === option.value;

            return (
              <div
                key={index}
                className={classNames(
                  styles.groupItem,
                  {
                    [styles.groupItemOneLine]: oneLine,
                  },
                  extraClasses && extraClasses.groupItem
                    ? extraClasses.groupItem ?? null
                    : null
                )}
              >
                <label
                  key={index}
                  className={classNames(
                    formStyles.label,
                    formStyles.labelInputOnly,
                    styles.label,
                    styles.labelInputOnly,
                    extraClasses && extraClasses.label
                      ? extraClasses.label ?? null
                      : null
                  )}
                >
                  <input
                    id={id}
                    type={type === 'checkbox_group' ? 'checkbox' : 'radio'}
                    name={fieldHandle}
                    value={option.value}
                    className={fieldClass}
                    disabled={
                      (type === 'checkbox_group' && disabled && !isChecked) ||
                      (type === 'radio_group' && disabled)
                    }
                    checked={isChecked}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => {
                      setHasFocus(true);
                      setTouched(true);
                    }}
                    onBlur={() => setHasFocus(false)}
                  />
                  <span>{option.label}</span>
                </label>
              </div>
            );
          });
        };

        return (
          <>
            {renderLabel()}
            <div
              className={classNames(
                styles.groupContent,
                extraClasses && extraClasses.groupContent
                  ? extraClasses.groupContent ?? null
                  : null
              )}
            >
              {renderGroupContent()}
            </div>
            {renderInstructions()}
            {renderErrors()}
          </>
        );
      }

      case ['text', 'textarea', 'email', 'number', 'select'].includes(type):
        return (
          <>
            {renderLabel()}
            {renderInput()}
            {renderInstructions()}
            {renderErrors()}
          </>
        );

      default:
        console.warn(`Unable to render form field for type "${type}"`);
        return <p>Unable to render form field for type "{type}"</p>;
    }
  };

  return <div className={columnClass}>{renderField()}</div>;
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
FormField.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'textarea',
    'file',
    'checkbox',
    'email',
    'radio_group',
    'checkbox_group',
    'select',
  ]),
  inputOnly: PropTypes.bool,
  oneLine: PropTypes.bool,
  position: PropTypes.oneOf(['left', 'center', 'right', 'spread']),
  required: PropTypes.bool,
  errors: PropTypes.array,
  handle: PropTypes.string,
  value: PropTypes.any,
  checked: PropTypes.bool,
  label: PropTypes.string,
  instructions: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.any,
    })
  ),
  onChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  step: PropTypes.number,
  standalone: PropTypes.bool,
  disabled: PropTypes.bool,
  initialErrors: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  classNames: PropTypes.shape({
    label: PropTypes.string,
    input: PropTypes.string,
    field: PropTypes.string,
    groupItem: PropTypes.string,
    groupContent: null,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
FormField.defaultProps = {
  type: 'text',
  inputOnly: false,
  oneLine: false,
  required: false,
  handle: null,
  errors: [],
  value: '',
  checked: false,
  label: null,
  instructions: null,
  placeholder: '',
  options: null,
  onChange: () => {},
  min: null,
  max: null,
  maxLength: null,
  step: null,
  standalone: false,
  disabled: false,
  initialErrors: false,
  id: null,
  className: '',
  classNames: {
    label: null,
    input: null,
    field: null,
    groupItem: null,
    groupContent: null,
  },
};

export default FormField;
